import { ReactNode } from "react";

import { Box, Chip, Stack, TableCell, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { AttendanceOverviewEntryResponse, AttendanceType, PurposeType } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import {
  formatAttendanceLabel,
  formatDayMonth,
  formatTimeRange,
  isAllDayRange,
  truncateNote,
} from "@/pages/attendance-list/AttendanceList.utils";
import { useTranslation } from "@/translations";

interface Props {
  entries: AttendanceOverviewEntryResponse[];
}

export function AttendanceDayCell({ entries }: Props) {
  const hasAbsence = entries.some((entry) => entry.attendanceType === AttendanceType.Absent);

  return (
    <TableCell
      align="center"
      sx={(theme) => ({
        verticalAlign: "middle",
        ...(hasAbsence && { bgcolor: alpha(theme.palette.fsw.yellow, 0.25) }),
      })}
    >
      <Stack spacing={0.5} alignItems="center">
        {entries.map((entry) => (
          <AttendanceEntry key={entry.appointmentId} entry={entry} />
        ))}
      </Stack>
    </TableCell>
  );
}

interface EntryLineProps {
  /** The entry's full note, or null when it has none. Only used to decide whether a tooltip is needed. */
  fullNote: string | null;
  fontWeight?: number;
  color?: string;
  children: ReactNode;
}

/**
 * One line of an entry. When the note is longer than the cell can show, the line becomes the trigger for
 * a tooltip holding the full text: `describeChild` adds it as a description via `aria-describedby`
 * instead of overwriting the accessible name, so a screen reader still reads the purpose, the time and
 * the return date. `tabIndex` keeps the tooltip reachable without a mouse.
 */
function EntryLine({ fullNote, fontWeight, color, children }: EntryLineProps) {
  const needsTooltip = fullNote !== null && fullNote !== truncateNote(fullNote);

  if (!needsTooltip) {
    return (
      <Typography variant="body2" fontWeight={fontWeight} color={color}>
        {children}
      </Typography>
    );
  }

  return (
    <Tooltip title={fullNote} describeChild>
      <Typography variant="body2" tabIndex={0} fontWeight={fontWeight} color={color}>
        {children}
      </Typography>
    </Tooltip>
  );
}

function AttendanceEntry({ entry }: { entry: AttendanceOverviewEntryResponse }) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { purposeLabelOf } = useAppointmentLabels();
  const locale = locales[language];

  // The note is whatever was typed into "Anwesenheit planen" and is shown on every entry type — it is
  // the only place that text surfaces outside the calendar.
  const fullNote = entry.details;
  const displayNote = fullNote !== null ? truncateNote(fullNote) : null;

  if (entry.attendanceType === AttendanceType.Absent) {
    // Null only when the absence run is still open at the edge of the backend's lookahead window.
    const returnDate = entry.reachableFromDate != null ? formatDayMonth(entry.reachableFromDate, locale) : null;

    return (
      <Stack spacing={0.25} alignItems="center">
        <EntryLine fullNote={fullNote}>
          <Box component="span" fontWeight={700}>
            {purposeLabelOf(entry.purposeKey)}
          </Box>
          {displayNote !== null && ` (${displayNote})`}
          {returnDate !== null && ` (${t("attendanceList.cell.reachableFrom", { date: returnDate })})`}
        </EntryLine>
        {entry.substituteCounselorUserCode && (
          <Typography variant="body2">
            {t("attendanceList.cell.substitute", { userCode: entry.substituteCounselorUserCode })}
          </Typography>
        )}
      </Stack>
    );
  }

  if (entry.purposeType === PurposeType.Attendance) {
    return (
      <Stack spacing={0.25} alignItems="center">
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography variant="body2" fontWeight={700}>
            {formatTimeRange(entry.startTimeUtc, entry.endTimeUtc, locale)}
          </Typography>
          {/* Every present attendance carries its tag, not just office attendance — an unlabelled
              "8–16" cannot answer whether someone is at their desk or in home office, which is the
              question the list exists to answer. */}
          <Chip
            size="small"
            label={formatAttendanceLabel(purposeLabelOf(entry.purposeKey))}
            sx={(theme) => ({ height: 20, bgcolor: theme.palette.fsw["blue-10"], fontWeight: 600 })}
          />
        </Box>
        {displayNote !== null && <EntryLine fullNote={fullNote}>{displayNote}</EntryLine>}
      </Stack>
    );
  }

  // A whole-day appointment (a multi-day Gericht, say) has no time range to show — printing one
  // renders as "0–0".
  const timeRange = isAllDayRange(entry.startTimeUtc, entry.endTimeUtc)
    ? null
    : ` ${formatTimeRange(entry.startTimeUtc, entry.endTimeUtc, locale)}`;

  return (
    <EntryLine fullNote={fullNote} fontWeight={600} color="secondary.main">
      {purposeLabelOf(entry.purposeKey)}
      {timeRange}
      {displayNote !== null && ` (${displayNote})`}
    </EntryLine>
  );
}
